import React from 'react'
import { IconButton } from '@material-ui/core'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'

const Pagination = ({ paginationInfo, onPageChange, onPageSizeChange }) => {
  if (!paginationInfo) return null

  const {
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    hasNextPage,
    hasPreviousPage,
  } = paginationInfo

  const pageSizeOptions = [10, 20, 50, 100] // Max 100 due to API limit

  const handlePageChange = newPage => {
    if (newPage >= 1 && newPage <= totalPages) {
      onPageChange(newPage)
    }
  }

  const renderPageNumbers = () => {
    const pages = []
    const maxVisiblePages = 5

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    // Add first page and ellipsis if needed
    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          style={{
            padding: '8px 12px',
            margin: '0 2px',
            backgroundColor: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
          }}
        >
          1
        </button>,
      )

      if (startPage > 2) {
        pages.push(
          <span
            key="ellipsis-start"
            style={{
              padding: '8px 4px',
              fontSize: '14px',
              color: '#6b7280',
            }}
          >
            ...
          </span>,
        )
      }
    }

    // Add visible page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          style={{
            padding: '8px 12px',
            margin: '0 2px',
            backgroundColor: i === currentPage ? '#3b82f6' : '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: i === currentPage ? '600' : '500',
            color: i === currentPage ? '#ffffff' : '#374151',
          }}
        >
          {i}
        </button>,
      )
    }

    // Add last page and ellipsis if needed
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <span
            key="ellipsis-end"
            style={{
              padding: '8px 4px',
              fontSize: '14px',
              color: '#6b7280',
            }}
          >
            ...
          </span>,
        )
      }

      pages.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          style={{
            padding: '8px 12px',
            margin: '0 2px',
            backgroundColor: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
          }}
        >
          {totalPages}
        </button>,
      )
    }

    return pages
  }

  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, totalItems)

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 0',
        borderTop: '1px solid #e2e8f0',
        marginTop: '20px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <span
          style={{
            fontSize: '14px',
            color: '#6b7280',
          }}
        >
          Showing {startItem} to {endItem} of {totalItems} designs
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px', color: '#374151' }}>Show:</span>
          <select
            value={itemsPerPage}
            onChange={e => onPageSizeChange(Number(e.target.value))}
            style={{
              padding: '4px 8px',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: '#ffffff',
              cursor: 'pointer',
            }}
          >
            {pageSizeOptions.map(size => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span style={{ fontSize: '14px', color: '#374151' }}>per page</span>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <IconButton
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={!hasPreviousPage}
          style={{
            padding: '8px',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            backgroundColor: '#ffffff',
          }}
        >
          <ChevronLeftIcon style={{ fontSize: '18px' }} />
        </IconButton>

        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
          {renderPageNumbers()}
        </div>

        <IconButton
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={!hasNextPage}
          style={{
            padding: '8px',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            backgroundColor: '#ffffff',
          }}
        >
          <ChevronRightIcon style={{ fontSize: '18px' }} />
        </IconButton>
      </div>
    </div>
  )
}

export default Pagination
